import {
  customFieldDefinitionListResponseSchema,
  customFieldDefinitionSchema,
  customFieldRecordContextSchema,
  type CustomFieldRecordContext,
  type CustomFieldRecordParams,
  customFieldGroupStateListResponseSchema,
  customFieldGroupStateSchema,
  type CustomFieldDefinition,
  type CustomFieldDefinitionInput,
  type CustomFieldDefinitionUpdateInput,
  type CustomFieldEntity,
  type CustomFieldPartnerRole,
  type CustomFieldGroupState,
  type DocumentType,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiFetch, apiSend, type ApiError } from '@/lib/api-client';

export const customFieldsQueryKey = ['custom-fields'] as const;
export const customFieldGroupStatesQueryKey = ['custom-field-group-states'] as const;

export function useCustomFieldGroupStates(): UseQueryResult<CustomFieldGroupState[], ApiError> {
  return useQuery({
    queryKey: customFieldGroupStatesQueryKey,
    queryFn: async () => {
      const response = await apiFetch(
        '/api/v1/custom-field-groups/states',
        customFieldGroupStateListResponseSchema,
      );
      return response.data;
    },
    staleTime: 300_000,
  });
}

export function useUpdateCustomFieldGroupState(): ReturnType<
  typeof useMutation<CustomFieldGroupState, ApiError, { groupId: string; isExpanded: boolean }>
> {
  const queryClient = useQueryClient();
  return useMutation<CustomFieldGroupState, ApiError, { groupId: string; isExpanded: boolean }>({
    mutationFn: ({ groupId, isExpanded }) =>
      apiFetch(`/api/v1/custom-field-groups/${groupId}/state`, customFieldGroupStateSchema, {
        method: 'PUT',
        body: { isExpanded },
      }),
    onMutate: async ({ groupId, isExpanded }) => {
      await queryClient.cancelQueries({ queryKey: customFieldGroupStatesQueryKey });
      queryClient.setQueryData<CustomFieldGroupState[]>(
        customFieldGroupStatesQueryKey,
        (current = []) => [
          ...current.filter((state) => state.groupId !== groupId),
          { groupId, isExpanded, updatedAt: new Date().toISOString() },
        ],
      );
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: customFieldGroupStatesQueryKey });
    },
  });
}

export function customFieldDefinitionsKey(
  entityType: CustomFieldEntity,
  documentType?: DocumentType,
  partnerRoles: readonly CustomFieldPartnerRole[] = [],
  locale = 'de',
): readonly unknown[] {
  return [
    ...customFieldsQueryKey,
    entityType,
    documentType ?? 'ALL',
    [...partnerRoles].sort().join(','),
    locale,
  ];
}

/** Belegfelder werden auf die Belegart eingegrenzt; ohne Angabe kommen alle Felder. */
export function useCustomFieldDefinitions(
  entityType: CustomFieldEntity = 'CUSTOMER',
  documentType?: DocumentType,
  partnerRoles: readonly CustomFieldPartnerRole[] = [],
): UseQueryResult<CustomFieldDefinition[], ApiError> {
  const { i18n } = useTranslation();
  const locale = i18n.language.split('-')[0] ?? 'de';
  return useQuery({
    queryKey: customFieldDefinitionsKey(entityType, documentType, partnerRoles, locale),
    queryFn: async () => {
      const params = new URLSearchParams({ entityType });
      if (documentType !== undefined) {
        params.set('documentType', documentType);
      }
      if (partnerRoles.length > 0) {
        params.set('partnerRoles', partnerRoles.join(','));
      }
      params.set('locale', locale);
      const response = await apiFetch(
        `/api/v1/custom-fields?${params.toString()}`,
        customFieldDefinitionListResponseSchema,
      );
      return response.data;
    },
    staleTime: 300_000,
  });
}

export function customFieldRecordKey(
  entityType: CustomFieldRecordParams['entityType'],
  recordId: string | null,
): readonly unknown[] {
  return [...customFieldsQueryKey, 'record', entityType, recordId ?? 'NEW'];
}

/** Unpublished availability is cached by owner; new forms only load published definitions. */
export function useMasterCustomFields(
  entityType: CustomFieldRecordParams['entityType'],
  recordId: string | null,
  partnerRoles: readonly CustomFieldPartnerRole[] = [],
): UseQueryResult<Pick<CustomFieldRecordContext, 'definitions' | 'availability'>, ApiError> {
  const { i18n } = useTranslation();
  const locale = i18n.language.split('-')[0] ?? 'de';
  return useQuery({
    queryKey: [
      ...customFieldRecordKey(entityType, recordId),
      [...partnerRoles].sort().join(','),
      locale,
    ],
    queryFn: async () => {
      if (recordId !== null) {
        const context = await apiFetch(
          `/api/v1/custom-fields/records/${entityType}/${encodeURIComponent(recordId)}?${new URLSearchParams({ locale }).toString()}`,
          customFieldRecordContextSchema,
        );
        const definitions = context.definitions.filter(
          (definition) =>
            entityType !== 'CUSTOMER' ||
            partnerRoles.length === 0 ||
            definition.partnerRoles.length === 0 ||
            definition.partnerRoles.some((role) => partnerRoles.includes(role)),
        );
        return { definitions, availability: context.availability };
      }
      const params = new URLSearchParams({ entityType, locale });
      if (partnerRoles.length > 0) params.set('partnerRoles', partnerRoles.join(','));
      const result = await apiFetch(
        `/api/v1/custom-fields?${params.toString()}`,
        customFieldDefinitionListResponseSchema,
      );
      return { definitions: result.data, availability: [] };
    },
    enabled: recordId !== '',
    staleTime: 60_000,
  });
}

export function useCreateCustomField(): ReturnType<
  typeof useMutation<CustomFieldDefinition, ApiError, CustomFieldDefinitionInput>
> {
  const queryClient = useQueryClient();

  return useMutation<CustomFieldDefinition, ApiError, CustomFieldDefinitionInput>({
    mutationFn: (input) =>
      apiFetch('/api/v1/custom-fields', customFieldDefinitionSchema, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customFieldsQueryKey });
    },
  });
}

export function useUpdateCustomField(): ReturnType<
  typeof useMutation<
    CustomFieldDefinition,
    ApiError,
    { id: string; input: CustomFieldDefinitionUpdateInput }
  >
> {
  const queryClient = useQueryClient();

  return useMutation<
    CustomFieldDefinition,
    ApiError,
    { id: string; input: CustomFieldDefinitionUpdateInput }
  >({
    mutationFn: ({ id, input }) =>
      apiFetch(`/api/v1/custom-fields/${id}`, customFieldDefinitionSchema, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customFieldsQueryKey });
    },
  });
}

export function useArchiveCustomField(): ReturnType<typeof useMutation<void, ApiError, string>> {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: (id) => apiSend(`/api/v1/custom-fields/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customFieldsQueryKey });
    },
  });
}
